/**
 * copyright-api.ts
 *
 * API client for copyright page XML generation endpoints
 */

import { apiRequest } from './api';

export interface CopyrightSearchResult {
  titleId: number;
  title: string;
  subtitle: string | null;
  authorNames: string | null;
  editorNames: string | null;
  isbnPrint: string | null;
  publishingHouse: string | null;
  copyrightYear: number | null;
}

export interface CopyrightPageData {
  titleId: number;
  mainEditionId: number;
  title: string;
  subtitle: string | null;
  shortTitle: string | null;
  languageCode: string | null;
  language: string | null;
  authorNames: string | null;
  editorNames: string | null;
  translatorNames: string | null;
  publishingHouseCode: string | null;
  publishingHouse: string | null;
  seriesName: string | null;
  volumeNo: string | null;
  isbnPrint: string | null;
  isbnEpdf: string | null;
  isbnEpub: string | null;
  doi: string | null;
  lccn: string | null;
  copyrightYear: number | null;
  openAccessType: string | null;
  openAccessYN: string | null;
}

/**
 * Search titles by ISBN, TitleId, or name
 */
export async function searchCopyrightTitles(
  query: string,
  limit: number = 20
): Promise<CopyrightSearchResult[]> {
  const params = new URLSearchParams({ q: query, limit: limit.toString() });
  const response = await apiRequest('GET', `/api/copyright-page/search?${params.toString()}`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to search titles');
  }

  const data = await response.json();
  return data.results;
}

/**
 * Get full copyright page data for a title
 */
export async function getCopyrightPageData(titleId: number): Promise<CopyrightPageData> {
  const response = await apiRequest('GET', `/api/copyright-page/${titleId}`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch title data');
  }

  return response.json();
}

/**
 * Get generated BITS XML for a title
 */
export async function getCopyrightPageXml(titleId: number): Promise<string> {
  const response = await apiRequest('GET', `/api/copyright-page/${titleId}/xml`);

  if (!response.ok) {
    // Error responses are JSON, but guard against unexpected content types
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('json')) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to generate XML');
    }
    throw new Error(`Failed to generate XML (HTTP ${response.status})`);
  }

  return response.text();
}

/**
 * Download BITS XML as a file
 */
export async function downloadCopyrightPageXml(titleId: number, title: string): Promise<void> {
  const xml = await getCopyrightPageXml(titleId);

  const blob = new Blob([xml], { type: 'application/xml' });
  const safeName = title.replace(/[^a-zA-Z0-9-_ ]/g, '').replace(/\s+/g, '-').substring(0, 50);
  const filename = `copyright-page-${titleId}-${safeName}.xml`;

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}
